import React, { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FooterAction,
  Icons,
} from '@ohif/ui-next';
import { useReportAssessment } from '../../hooks/useReportAssessment';
import FindingsAssessment from './FindingsAssessment';
import NoFindingsAssessment from './NoFindingsAssessment';
import { AssessmentDialogProps } from './types';
import { ANCHOR } from '../dialogAnchor';

function focusFirstUnansweredVerdict(event: Event): void {
  const content = event.currentTarget as HTMLElement | null;

  if (!content) {
    return;
  }

  const target =
    content.querySelector<HTMLElement>('[data-sycai-verdict="unanswered"] [role="radio"]') ??
    content;

  event.preventDefault();
  target.focus();
}

export default function AssessmentDialog({
  open,
  onOpenChange,
  studyInstanceUid,
  returnFocusRef,
}: AssessmentDialogProps) {
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const { rows, isNoFindings, isSubmitDisabled, setClassification, setDetection, submit } =
    useReportAssessment(studyInstanceUid, close);

  const returnFocusToTrigger = useCallback(
    (event: Event) => {
      event.preventDefault();
      returnFocusRef.current?.focus();
    },
    [returnFocusRef]
  );

  const submitIfEnabled = useCallback(() => {
    if (!isSubmitDisabled) {
      submit();
    }
  }, [isSubmitDisabled, submit]);

  return (
    <Dialog
      open={open}
      modal={false}
      onOpenChange={onOpenChange}
      showOverlay={false}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      <DialogContent
        className="border-primary border sm:max-h-[270px] sm:max-w-[400px]"
        style={ANCHOR}
        onOpenAutoFocus={focusFirstUnansweredVerdict}
        onCloseAutoFocus={returnFocusToTrigger}
        data-cy="sycai-assessment-dialog"
      >
        <DialogHeader>
          <DialogTitle>Report Assessment</DialogTitle>
          <DialogDescription className="sr-only">
            Record a Yes or No verdict for each finding, then submit.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-[170px] flex-col gap-4 overflow-y-auto pt-2">
          {isNoFindings ? (
            <NoFindingsAssessment
              row={rows[0]}
              onDetectionChange={value => setDetection(0, value)}
            />
          ) : (
            <FindingsAssessment
              rows={rows}
              onClassificationChange={setClassification}
              onDetectionChange={setDetection}
            />
          )}
        </div>

        <FooterAction className="gap-3 pt-1 pb-2">
          <FooterAction.Secondary
            className="bg-primary/60 hover:bg-primary/100 flex-1 text-sm hover:text-white"
            dataCY="sycai-assessment-close"
            onClick={close}
          >
            Save &amp; Close
          </FooterAction.Secondary>

          <FooterAction.Primary
            className="flex-1 bg-green-500 text-sm hover:bg-green-500/90 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            dataCY="sycai-assessment-submit"
            onClick={submitIfEnabled}
            aria-disabled={isSubmitDisabled || undefined}
          >
            <Icons.StatusSuccess
              aria-hidden="true"
              className="mr-1 h-4 w-4"
            />
            Submit
          </FooterAction.Primary>
        </FooterAction>
      </DialogContent>
    </Dialog>
  );
}
